import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Search, X, RefreshCw, Calendar, FileSpreadsheet, ExternalLink } from 'lucide-react';
import * as XLSX from 'xlsx';
import { vouchersApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast/Toast';

const fmt = (n: any) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const toInputDate = (d: Date) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const displayDate = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

type Row = { ledger_id: number; party_name: string; entry_count: number; net_amount: number; opening_amount: number; total_amount: number; last_date: string | null };
type SortKey = 'party_name' | 'entry_count' | 'net_amount';
type SortDir = 'asc' | 'desc';
const STORAGE_KEY = 'on-account-filters';

type GroupNode = { id: number; name: string; parent_id: number | null };
/** Flatten the ledger-group tree depth-first with indentation for a <select>. */
function flattenGroups(groups: GroupNode[]): { id: number; label: string }[] {
  const byParent = new Map<number | null, GroupNode[]>();
  for (const g of groups) {
    if (!byParent.has(g.parent_id)) byParent.set(g.parent_id, []);
    byParent.get(g.parent_id)!.push(g);
  }
  byParent.forEach(arr => arr.sort((a, b) => a.name.localeCompare(b.name)));
  const ids = new Set(groups.map(g => g.id));
  const roots = groups.filter(g => g.parent_id == null || !ids.has(g.parent_id)).sort((a, b) => a.name.localeCompare(b.name));
  const out: { id: number; label: string }[] = [];
  const seen = new Set<number>();
  const walk = (node: GroupNode, depth: number) => {
    if (seen.has(node.id)) return;
    seen.add(node.id);
    out.push({ id: node.id, label: `${'  '.repeat(depth)}${depth ? '└ ' : ''}${node.name}` });
    for (const child of byParent.get(node.id) || []) walk(child, depth + 1);
  };
  for (const r of roots) walk(r, 0);
  return out;
}

export default function OnAccountReport() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { showError } = useToast();
  const initial = (() => {
    try { const s = localStorage.getItem(STORAGE_KEY); if (s) return JSON.parse(s); } catch { /* ignore */ }
    return { asOf: toInputDate(new Date()), search: '' };
  })();

  const [asOf, setAsOf] = useState<string>(initial.asOf || toInputDate(new Date()));
  const [search, setSearch] = useState<string>(initial.search || '');
  const [debouncedSearch, setDebouncedSearch] = useState<string>(initial.search || '');
  const [groupId, setGroupId] = useState<number | ''>(initial.groupId ?? '');
  const [groups, setGroups] = useState<GroupNode[]>([]);
  const flatGroups = useMemo(() => flattenGroups(groups), [groups]);
  useEffect(() => {
    vouchersApi.getLedgerGroupTree().then(r => { if ((r as any)?.success) setGroups((r as any).data || []); }).catch(() => {});
  }, []);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [totals, setTotals] = useState({ entry_count: 0, net_dr: 0, net_cr: 0, opening: 0 });
  const [detail, setDetail] = useState<Row | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('net_amount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ asOf, search, groupId })); } catch { /* ignore */ } }, [asOf, search, groupId]);
  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search), 300); return () => clearTimeout(t); }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vouchersApi.getOnAccount({ as_of: asOf || undefined, search: debouncedSearch || undefined, group_id: groupId ? Number(groupId) : undefined });
      if (res.success) {
        setRows(res.data.rows || []);
        setTotals({
          entry_count: res.data.totals?.entry_count || 0,
          net_dr: res.data.totals?.net_dr || 0,
          net_cr: res.data.totals?.net_cr || 0,
          opening: (res.data.totals as any)?.opening || 0,
        });
      }
    } catch { showError('Error', 'Failed to load On Account report'); }
    finally { setLoading(false); }
  }, [asOf, debouncedSearch, groupId, showError]);
  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => {
    const arr = [...rows];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const av: any = sortKey === 'net_amount' ? Math.abs(a.total_amount) : (a as any)[sortKey];
      const bv: any = sortKey === 'net_amount' ? Math.abs(b.total_amount) : (b as any)[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });
    return arr;
  }, [rows, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => { if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(k); setSortDir('desc'); } };

  const exportExcel = () => {
    if (!isAdmin() || sorted.length === 0) return;
    const data = sorted.map((r, i) => ({
      '#': i + 1, 'Party': r.party_name, 'Entries': r.entry_count,
      'On-Account': r.net_amount, 'Opening (non-bill-wise)': r.opening_amount,
      'Total': Math.abs(r.total_amount), 'Dr/Cr': r.total_amount >= 0 ? 'Dr' : 'Cr',
      'Last Date': displayDate(r.last_date),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'On Account');
    XLSX.writeFile(wb, `On-Account-${toInputDate(new Date())}.xlsx`);
  };

  const cell = 'border border-slate-300 px-2.5 py-1.5 text-[14px] leading-snug';
  const cellNum = `${cell} text-right tabular-nums whitespace-nowrap`;
  const headCell = 'border border-slate-400 bg-slate-200 px-2.5 py-1.5 text-[13px] font-bold text-slate-700 uppercase tracking-wide sticky top-0 z-10';

  return (
    <div className="flex flex-col w-full fixed left-0 right-0 top-14 bottom-16 sm:static sm:h-full sm:top-auto sm:bottom-auto" style={{ overscrollBehavior: 'contain' }}>
      {/* Header */}
      <div className="flex-none bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 -ml-1" title="Back"><ChevronLeft size={20} /></button>
          <h1 className="text-[17px] font-bold text-slate-800">On Account</h1>
          <span className="ml-2 text-[11px] text-slate-400 hidden sm:inline">Amounts with no bill reference</span>
        </div>
        <div className="flex items-center gap-1">
          {isAdmin() && (
            <button onClick={exportExcel} disabled={loading || sorted.length === 0}
              className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 disabled:opacity-40" title="Export to Excel"><FileSpreadsheet size={18} /></button>
          )}
          <button onClick={load} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Refresh"><RefreshCw size={16} className={loading ? 'animate-spin' : ''} /></button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex-none bg-slate-50 border-b border-slate-200 px-3 py-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[160px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search party name…"
              className="pl-9 pr-8 py-2 border border-slate-300 rounded-lg text-[13px] w-full outline-none focus:ring-2 focus:ring-blue-300 bg-white" />
            {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={13} /></button>}
          </div>
          {/* Group filter — main group + all nested sub-groups (hierarchy). */}
          <select value={groupId} onChange={e => setGroupId(e.target.value ? Number(e.target.value) : '')}
            className="min-w-[150px] text-[13px] border border-slate-300 rounded-lg px-3 py-2 bg-white outline-none">
            <option value="">All groups</option>
            {flatGroups.map(g => <option key={g.id} value={g.id}>{g.label}</option>)}
          </select>
          <div className="flex items-stretch border border-slate-300 rounded-lg bg-white overflow-hidden">
            <div className="flex items-center gap-1 px-2 border-r border-slate-300 bg-slate-100">
              <Calendar size={12} className="text-slate-400" /><span className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">As of</span>
            </div>
            <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} className="text-[13px] text-slate-700 outline-none bg-transparent px-2 py-1.5 w-[125px]" />
          </div>
          <button onClick={() => setAsOf(toInputDate(new Date()))} className="px-3 py-2 border border-slate-300 rounded-lg bg-white text-[13px] text-slate-600 hover:bg-slate-100">Today</button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-auto bg-white border-x border-b border-slate-300 mx-3 my-2">
        <table className="border-collapse text-[14px] w-full h-full">
          <thead>
            <tr>
              <th className={`${headCell} text-center w-10`}>#</th>
              <th className={`${headCell} text-left cursor-pointer`} onClick={() => toggleSort('party_name')}>Party</th>
              <th className={`${headCell} text-right w-20 cursor-pointer`} onClick={() => toggleSort('entry_count')}>Entries</th>
              <th className={`${headCell} text-right w-36`}>On-Account</th>
              <th className={`${headCell} text-right w-36`} title="Ledger opening that was not entered bill-wise">Opening</th>
              <th className={`${headCell} text-right w-36 cursor-pointer`} onClick={() => toggleSort('net_amount')}>Total</th>
              <th className={`${headCell} text-center w-14`}>Dr/Cr</th>
              <th className={`${headCell} text-left w-28`}>Last Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className={`${cell} text-center text-slate-400 py-8`}>Loading…</td></tr>
            ) : sorted.length === 0 ? (
              <tr><td colSpan={8} className={`${cell} text-center text-slate-400 py-8`}>No on-account (unallocated) amounts as of {displayDate(asOf)}</td></tr>
            ) : sorted.map((r, i) => {
              const positive = r.total_amount >= 0;
              return (
                <tr key={r.ledger_id} className={`${i % 2 ? 'bg-slate-50' : 'bg-white'} hover:bg-blue-50 cursor-pointer`} onClick={() => setDetail(r)}>
                  <td className={`${cell} text-center text-slate-400 bg-slate-100 tabular-nums`}>{i + 1}</td>
                  <td className={`${cell} font-medium text-slate-800`}>{r.party_name}</td>
                  <td className={cellNum + ' text-slate-600'}>{r.entry_count || <span className="text-slate-300">—</span>}</td>
                  <td className={cellNum + ' text-slate-600'}>{Math.abs(r.net_amount) > 0.005 ? fmt(Math.abs(r.net_amount)) : <span className="text-slate-300">—</span>}</td>
                  <td className={cellNum + ' text-amber-700'} title="Non-bill-wise ledger opening">{Math.abs(r.opening_amount) > 0.005 ? fmt(Math.abs(r.opening_amount)) : <span className="text-slate-300">—</span>}</td>
                  <td className={cellNum + (positive ? ' text-emerald-700' : ' text-red-700') + ' font-semibold'}>{fmt(Math.abs(r.total_amount))}</td>
                  <td className={`${cell} text-center font-semibold ${positive ? 'text-emerald-700' : 'text-red-700'}`}>{positive ? 'Dr' : 'Cr'}</td>
                  <td className={`${cell} text-slate-600 whitespace-nowrap tabular-nums`}>{displayDate(r.last_date)}</td>
                </tr>
              );
            })}
            {!loading && sorted.length > 0 && <tr aria-hidden="true"><td colSpan={8} className="h-full p-0 bg-white border-x border-slate-300"></td></tr>}
          </tbody>
          {!loading && sorted.length > 0 && (
            <tfoot>
              <tr className="bg-slate-200 font-bold sticky bottom-0">
                <td className={cell + ' text-slate-500'}>—</td>
                <td className={`${cell} text-slate-700`}>Total ({sorted.length} parties)</td>
                <td className={cellNum + ' text-slate-700'}>{totals.entry_count}</td>
                <td className={cell}></td>
                <td className={cellNum + ' text-amber-700'} title="Opening included from non-bill-wise ledger openings">{fmt(Math.abs(totals.opening))}</td>
                <td className={cell + ' text-right text-[12px]'}>
                  <span className="text-emerald-700">Dr {fmt(totals.net_dr)}</span>
                  <span className="mx-1 text-slate-400">·</span>
                  <span className="text-red-700">Cr {fmt(totals.net_cr)}</span>
                </td>
                <td className={cell}></td>
                <td className={cell}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {detail && <OnAccountDetailModal row={detail} asOf={asOf} onClose={() => setDetail(null)} />}
    </div>
  );
}

function OnAccountDetailModal({ row, asOf, onClose }: { row: Row; asOf: string; onClose: () => void }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [txns, setTxns] = useState<any[]>([]);
  const [closing, setClosing] = useState(0);
  useEffect(() => {
    let alive = true; setLoading(true);
    vouchersApi.getOnAccountDetail(row.ledger_id, asOf)
      .then(r => { if (alive && r.success) { setTxns(r.data.transactions || []); setClosing(r.data.closing || 0); } })
      .catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [row.ledger_id, asOf]);
  const positive = closing >= 0;
  const openVoucher = (id: number | null) => { if (!id) return; onClose(); navigate(`/billing/vouchers/edit/${id}`); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-3.5 border-b border-slate-200">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">On Account · no bill reference</div>
            <div className="text-[16px] font-bold text-slate-800 truncate">{row.party_name}</div>
            <div className="text-[12px] text-slate-500">{loading ? 'Loading…' : `${txns.length} entr${txns.length === 1 ? 'y' : 'ies'}`}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-auto px-2 sm:px-4 py-3">
          {loading ? (
            <div className="py-14 text-center text-slate-400 text-sm"><RefreshCw size={20} className="animate-spin mx-auto mb-2 text-blue-400" /> Loading…</div>
          ) : txns.length === 0 ? (
            <div className="py-14 text-center text-slate-400 text-sm">No on-account entries.</div>
          ) : (
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="bg-slate-100 text-slate-600">
                  <th className="text-left px-2 py-1.5 border border-slate-200 font-semibold">Date</th>
                  <th className="text-left px-2 py-1.5 border border-slate-200 font-semibold">Vch No</th>
                  <th className="text-left px-2 py-1.5 border border-slate-200 font-semibold">Type</th>
                  <th className="text-right px-2 py-1.5 border border-slate-200 font-semibold">Debit</th>
                  <th className="text-right px-2 py-1.5 border border-slate-200 font-semibold">Credit</th>
                  <th className="text-right px-2 py-1.5 border border-slate-200 font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody>
                {txns.map(t => (
                  <tr key={t.id} className="hover:bg-blue-50">
                    <td className="px-2 py-1.5 border border-slate-200 whitespace-nowrap tabular-nums text-slate-600">{displayDate(t.vch_date)}</td>
                    <td className="px-2 py-1.5 border border-slate-200 whitespace-nowrap">
                      {t.vch_id ? (
                        <button onClick={() => openVoucher(t.vch_id)} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline font-medium">
                          {t.vch_no || `#${t.vch_id}`}<ExternalLink size={11} />
                        </button>
                      ) : <span className={t.is_opening ? 'text-slate-500 italic' : 'text-slate-500'}>{t.vch_no || '—'}</span>}
                    </td>
                    <td className={`px-2 py-1.5 border border-slate-200 whitespace-nowrap ${t.is_opening ? 'text-slate-500 italic' : 'text-slate-600'}`}>{t.vch_type || '—'}</td>
                    <td className="px-2 py-1.5 border border-slate-200 text-right tabular-nums text-slate-700">{t.debit ? fmt(t.debit) : ''}</td>
                    <td className="px-2 py-1.5 border border-slate-200 text-right tabular-nums text-slate-700">{t.credit ? fmt(t.credit) : ''}</td>
                    <td className="px-2 py-1.5 border border-slate-200 text-right tabular-nums font-medium text-slate-800">{fmt(Math.abs(t.running_balance))} {t.running_balance >= 0 ? 'Dr' : 'Cr'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between">
          <span className="text-[12px] text-slate-400">On-Account balance{' '}
            <span className={positive ? 'text-emerald-700 font-semibold' : 'text-red-600 font-semibold'}>₹{fmt(Math.abs(closing))} {positive ? 'Dr' : 'Cr'}</span>
          </span>
          <button onClick={onClose} className="px-4 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-[13px] font-medium">Close</button>
        </div>
      </div>
    </div>
  );
}
